import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import { COURSES_QUERY_KEY, type RawCourse } from './useCourses';

interface AddCoursePayload {
  course: {
    instructor_id: string;
    name: string;
    parent_subject: string;
    type: number;
  };
}

interface RawAddCourseResponse {
  course?: RawCourse | null;
}

export interface CreateCourseInput {
  courseName: string;
  subject: string;
}

export interface CreateCourseResult {
  course: RawCourse;
  courseId: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

export function getCourseIdentifier(course: RawCourse | null | undefined) {
  return normalizeString(course?.id) ?? normalizeString(course?.uid);
}

export function unwrapCreatedCourse(
  response:
    | RawAddCourseResponse
    | RawCourse
    | LegacyKuduResponseEnvelope<RawAddCourseResponse | RawCourse>
    | null
    | undefined,
) {
  if (!response || !isRecord(response)) {
    return null;
  }

  if ('Response' in response) {
    return unwrapCreatedCourse(
      response.Response as RawAddCourseResponse | RawCourse | null,
    );
  }

  if ('course' in response && isRecord(response.course)) {
    return response.course as RawCourse;
  }

  if ('id' in response || 'uid' in response || 'name' in response) {
    return response as RawCourse;
  }

  return null;
}

export function buildAddCoursePayload({
  courseName,
  instructorId,
  subject,
}: {
  courseName: string;
  instructorId: string;
  subject: string;
}): AddCoursePayload {
  return {
    course: {
      instructor_id: instructorId.trim(),
      name: courseName.trim(),
      parent_subject: subject.trim(),
      type: 2,
    },
  };
}

export function useCreateCourse() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<CreateCourseResult, ApiError, CreateCourseInput>({
    mutationFn: async ({ courseName, subject }) => {
      const normalizedCourseName = courseName.trim();
      const normalizedSubject = subject.trim();

      if (!normalizedCourseName) {
        throw createDataError('Enter a course name before creating a course.');
      }

      if (!normalizedSubject) {
        throw createDataError('Choose a subject area before creating a course.');
      }

      if (!user) {
        throw createDataError('You must be signed in to create a course.');
      }

      const instructorId = getKuduUserId(user) ?? user.uid;

      if (!instructorId) {
        throw createDataError('Unable to determine the current instructor ID.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const response = await client.invokeKuduMethod<
        RawAddCourseResponse | LegacyKuduResponseEnvelope<RawAddCourseResponse>,
        AddCoursePayload
      >(
        'AddCourse',
        buildAddCoursePayload({
          courseName: normalizedCourseName,
          instructorId,
          subject: normalizedSubject,
        }),
      );

      const course = unwrapCreatedCourse(response);
      const courseId = getCourseIdentifier(course);

      if (!course || !courseId) {
        throw createDataError(
          'The course was created, but the response did not include a usable course ID.',
        );
      }

      return {
        course,
        courseId,
      };
    },
    onSuccess: async () => {
      // refetch, not invalidate: the course list is not mounted while the
      // instructor is on this page, and invalidating an inactive query only
      // marks it stale. The list would then mount showing the cached courses
      // and fill in the new one a moment later, which read as a slow save.
      await queryClient.refetchQueries({
        queryKey: COURSES_QUERY_KEY,
      });

      navigate('/app/courses');
    },
  });
}
